import type { Cell } from "../../game-core/src/index";
import type {
  MapValidationReport,
  InvalidRowReport,
  SpawnId,
  UnreachableFloorRegion,
  UnsafeSpawnReport,
} from "./content-types";
import { SPAWN_IDS, type MapManifest } from "./content-types";

export const CONTENT_MAP_WIDTH = 13;
export const CONTENT_MAP_HEIGHT = 11;

const FLOOR_TILES = new Set([".", "0", " "]);
const DESTRUCTIBLE_TILES = new Set(["+", "B", "?"]);
const KNOWN_TILES = new Set([".", "0", " ", "#", "X", "+", "B", "?"]);

const DIRECTIONS: readonly Cell[] = Object.freeze([
  Object.freeze({ x: 0, y: -1 }),
  Object.freeze({ x: 0, y: 1 }),
  Object.freeze({ x: -1, y: 0 }),
  Object.freeze({ x: 1, y: 0 }),
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function cellKey(cell: Cell): string {
  return `${cell.x},${cell.y}`;
}

function readCell(value: unknown): Cell | null {
  if (!isRecord(value)) {
    return null;
  }
  const x = value["x"];
  const y = value["y"];
  if (typeof x !== "number" || typeof y !== "number") {
    return null;
  }
  return { x, y };
}

function isInside(cell: Cell): boolean {
  return (
    Number.isInteger(cell.x) &&
    Number.isInteger(cell.y) &&
    cell.x >= 0 &&
    cell.y >= 0 &&
    cell.x < CONTENT_MAP_WIDTH &&
    cell.y < CONTENT_MAP_HEIGHT
  );
}

function tileAt(rows: readonly unknown[], cell: Cell): string | undefined {
  if (!isInside(cell)) {
    return undefined;
  }
  const row = rows[cell.y];
  return typeof row === "string" ? row[cell.x] : undefined;
}

function isFloorAt(rows: readonly unknown[], cell: Cell): boolean {
  const tile = tileAt(rows, cell);
  return tile !== undefined && FLOOR_TILES.has(tile);
}

function isDestructibleAt(rows: readonly unknown[], cell: Cell): boolean {
  const tile = tileAt(rows, cell);
  return tile !== undefined && DESTRUCTIBLE_TILES.has(tile);
}

function adjacentCells(cell: Cell): readonly Cell[] {
  return DIRECTIONS.map((direction) => ({
    x: cell.x + direction.x,
    y: cell.y + direction.y,
  }));
}

function inspectRows(rows: readonly unknown[]): readonly InvalidRowReport[] {
  const invalidRows: InvalidRowReport[] = [];

  for (let y = 0; y < CONTENT_MAP_HEIGHT; y += 1) {
    const row = rows[y];
    if (typeof row !== "string") {
      invalidRows.push({
        row: y,
        expectedWidth: CONTENT_MAP_WIDTH,
        actualWidth: 0,
        reason: "missing row",
      });
      continue;
    }
    if (row.length !== CONTENT_MAP_WIDTH) {
      invalidRows.push({
        row: y,
        expectedWidth: CONTENT_MAP_WIDTH,
        actualWidth: row.length,
        reason: "row has the wrong width",
      });
      continue;
    }

    for (let x = 0; x < CONTENT_MAP_WIDTH; x += 1) {
      const tile = row[x];
      if (tile === undefined || !KNOWN_TILES.has(tile)) {
        invalidRows.push({
          row: y,
          expectedWidth: CONTENT_MAP_WIDTH,
          actualWidth: row.length,
          reason: `unknown tile at ${x},${y}`,
        });
      }
    }
  }

  for (let y = CONTENT_MAP_HEIGHT; y < rows.length; y += 1) {
    const row = rows[y];
    invalidRows.push({
      row: y,
      expectedWidth: CONTENT_MAP_WIDTH,
      actualWidth: typeof row === "string" ? row.length : 0,
      reason: "unexpected extra row",
    });
  }

  return invalidRows;
}

function collectUnsafeSpawns(
  rows: readonly unknown[],
  definition: MapManifest,
): readonly UnsafeSpawnReport[] {
  const unsafeSpawns: UnsafeSpawnReport[] = [];
  const rawDefinition = definition as unknown as Record<string, unknown>;
  const rawSpawns = isRecord(rawDefinition["spawns"])
    ? rawDefinition["spawns"]
    : {};
  const seen = new Map<string, SpawnId>();

  for (const player of SPAWN_IDS) {
    const cell = readCell(rawSpawns[player]);
    const reasons: string[] = [];

    if (cell === null) {
      reasons.push("spawn is missing or malformed");
    } else if (!isInside(cell)) {
      reasons.push("spawn is outside the 13x11 map");
    } else {
      if (!isFloorAt(rows, cell)) {
        reasons.push("spawn cell is not floor");
      }

      const escapeRoutes = adjacentCells(cell).filter((neighbor) =>
        isFloorAt(rows, neighbor),
      ).length;
      if (escapeRoutes < 2) {
        reasons.push("spawn has fewer than two floor escape routes");
      }

      for (const neighbor of adjacentCells(cell)) {
        if (isDestructibleAt(rows, neighbor)) {
          reasons.push("spawn buffer contains a destructible tile");
          break;
        }
      }

      const key = cellKey(cell);
      const otherPlayer = seen.get(key);
      if (otherPlayer !== undefined) {
        reasons.push(`spawn overlaps ${otherPlayer}`);
      } else {
        seen.set(key, player);
      }
    }

    if (reasons.length > 0) {
      unsafeSpawns.push({
        player,
        cell,
        reason: reasons.join(", "),
      });
    }
  }

  return unsafeSpawns;
}

function collectUnreachableRegions(
  rows: readonly unknown[],
  definition: MapManifest,
): readonly UnreachableFloorRegion[] {
  const rawDefinition = definition as unknown as Record<string, unknown>;
  const rawSpawns = isRecord(rawDefinition["spawns"])
    ? rawDefinition["spawns"]
    : {};
  const spawnCells = SPAWN_IDS.map((player) =>
    readCell(rawSpawns[player]),
  ).filter((cell): cell is Cell => cell !== null && isInside(cell));
  const visited = new Set<string>();
  const regions: UnreachableFloorRegion[] = [];

  for (let y = 0; y < CONTENT_MAP_HEIGHT; y += 1) {
    for (let x = 0; x < CONTENT_MAP_WIDTH; x += 1) {
      const start = { x, y };
      const startKey = cellKey(start);
      if (!isFloorAt(rows, start) || visited.has(startKey)) {
        continue;
      }

      const queue: Cell[] = [start];
      const cells: Cell[] = [];
      visited.add(startKey);

      for (let index = 0; index < queue.length; index += 1) {
        const current = queue[index];
        if (current === undefined) {
          continue;
        }
        cells.push(current);

        for (const neighbor of adjacentCells(current)) {
          const key = cellKey(neighbor);
          if (isFloorAt(rows, neighbor) && !visited.has(key)) {
            visited.add(key);
            queue.push(neighbor);
          }
        }
      }

      const touchesSpawn = cells.some((cell) =>
        spawnCells.some((spawn) => cellKey(spawn) === cellKey(cell)),
      );
      if (!touchesSpawn) {
        const anchor = cells[0];
        if (anchor !== undefined) {
          regions.push({
            anchor,
            size: cells.length,
            cells: Object.freeze(cells),
          });
        }
      }
    }
  }

  return regions;
}

export function validateMapManifest(
  definition: MapManifest,
): MapValidationReport {
  const rawDefinition = definition as unknown as Record<string, unknown>;
  const rawRows = rawDefinition["rows"];
  const rows: readonly unknown[] = Array.isArray(rawRows) ? rawRows : [];
  const invalidRows = inspectRows(rows);
  const unsafeSpawns = collectUnsafeSpawns(rows, definition);
  const unreachableFloorRegions = collectUnreachableRegions(rows, definition);
  const mapId =
    typeof rawDefinition["id"] === "string"
      ? rawDefinition["id"]
      : "unknown-map";
  const errors = [
    ...invalidRows.map((row) => `row ${row.row}: ${row.reason}`),
    ...unsafeSpawns.map((spawn) => `${spawn.player} spawn: ${spawn.reason}`),
    ...unreachableFloorRegions.map(
      (region) =>
        `unreachable floor region at ${region.anchor.x},${region.anchor.y} (${region.size} cells)`,
    ),
  ];

  return Object.freeze({
    mapId,
    valid:
      invalidRows.length === 0 &&
      unsafeSpawns.length === 0 &&
      unreachableFloorRegions.length === 0,
    invalidRows: Object.freeze([...invalidRows]),
    unsafeSpawns: Object.freeze([...unsafeSpawns]),
    unreachableFloorRegions: Object.freeze([...unreachableFloorRegions]),
    errors: Object.freeze(errors),
  });
}

export const validateMap = validateMapManifest;
